/* eslint-disable semi, no-await-in-loop, no-restricted-syntax, no-restricted-globals, no-underscore-dangle, no-plusplus */
/* ============================================================================
 * QA automation bridge — lets the admin panel's "Testing" page drive the REAL
 * app UI like a human (login, etc.), entirely client-side, with no extra server.
 *
 * How it works: the admin Testing page embeds this app in an iframe at
 *   https://projector.app/newSign-in?qa_bridge=1
 * This module (only when ?qa_bridge=1 is present) performs the real on-screen
 * actions — typing into inputs, clicking buttons — inside THIS app's own page
 * (same-origin to itself, so it's allowed), and reports each step back to the
 * admin page via postMessage. Turnstile passes because it's a real user browser.
 *
 * SAFETY:
 *  - Does nothing unless the URL has ?qa_bridge=1.
 *  - Only obeys messages from an allow-listed admin origin.
 *  - Holds NO credentials: the admin page supplies email/password per run.
 *  - Only performs actions a human could already do on the form; it never reads
 *    out tokens/cookies or exposes anything the page doesn't already show.
 * ========================================================================== */

// Deploy marker — on ANY load of the app, `window.__pjQaBridge` becomes 'loaded'.
// Quick check that a build WITH the bridge is actually live: open the app and
// run `window.__pjQaBridge` in the console (undefined = old bundle still cached).
try { window.__pjQaBridge = 'loaded' } catch { /* noop */ }

const ALLOWED_ORIGINS = [
  'http://localhost:5173',
  'http://localhost:4173',
  'http://127.0.0.1:5173',
  'https://admin-projector.eq7.in',
  'https://admin.projectorstream.com',
]

/* ---------- tiny DOM helpers (human-like) ---------- */
const sleep = (ms) => new Promise((r) => setTimeout(r, ms))
const isVisible = (el) => !!el && !!el.offsetParent && el.getClientRects().length > 0

async function waitFor(getter, { timeout = 15000, interval = 200 } = {}) {
  const end = Date.now() + timeout
  for (;;) {
    let v
    try { v = getter() } catch { v = null }
    if (v) return v
    if (Date.now() >= end) throw new Error('timeout')
    await sleep(interval)
  }
}

function visibleByPlaceholder(ph) {
  return Array.from(document.querySelectorAll(`input[placeholder="${ph}"]`)).find(isVisible) || null
}
function enabledButtonWithText(re) {
  return Array.from(document.querySelectorAll('button')).find(
    (b) => isVisible(b) && !b.disabled && re.test((b.textContent || '').trim()),
  ) || null
}
function pageHasText(re) {
  return re.test(document.body ? document.body.innerText : '')
}

// React tracks the input's value internally; set it via the native setter and
// dispatch a real input event so React's onChange fires.
function setNativeValue(el, value) {
  const proto = el instanceof window.HTMLTextAreaElement ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype
  const setter = Object.getOwnPropertyDescriptor(proto, 'value').set
  setter.call(el, value)
  el.dispatchEvent(new Event('input', { bubbles: true }))
}
async function humanType(el, text) {
  el.focus()
  setNativeValue(el, '')
  let acc = ''
  for (const ch of String(text)) {
    acc += ch
    setNativeValue(el, acc)
    await sleep(35)
  }
  el.dispatchEvent(new Event('change', { bubbles: true }))
  if (typeof el.blur === 'function') el.blur()
}

function requireField(v, name) {
  if (!v) throw new Error(`No ${name} supplied by the admin page.`)
  return v
}

// Start every QA run from a logged-out session so the sign-in form actually
// shows (otherwise a token left over from a previous run keeps us "logged in").
// Targeted: only auth-ish keys, so we don't wipe unrelated state. Runs at init,
// before React reads the token, so the app boots signed out.
function clearAuthForFreshSession() {
  const EXACT = ['token', 'uploader_token', 'uploader_token_expiry', 'user_uuid']
  try {
    const ls = window.localStorage
    for (const k of EXACT) ls.removeItem(k)
    for (const k of Object.keys(ls)) {
      if (/token|auth|session|logged|uuid/i.test(k)) ls.removeItem(k)
    }
  } catch { /* storage blocked */ }
  try { window.sessionStorage.clear() } catch { /* noop */ }
}

/* ---------- flows ---------- */
// Each step: { title, hint, run(cfg) } — returns a detail string or throws.
const FLOWS = {
  login: {
    title: 'Login',
    steps: [
      {
        title: 'Open sign-in page',
        hint: 'wait for form',
        run: async () => {
          await waitFor(() => pageHasText(/Welcome back\. Sign in to continue to your Projector\./) || visibleByPlaceholder('Email'), { timeout: 25000 })
          return `loaded ${location.pathname}`
        },
      },
      {
        title: 'Enter email',
        hint: 'type email · existence probe',
        run: async (cfg) => {
          const email = requireField(cfg.email, 'email')
          const input = await waitFor(() => visibleByPlaceholder('Email'), { timeout: 15000 })
          await humanType(input, email)
          return email
        },
      },
      {
        title: 'Pass bot check',
        hint: 'Cloudflare Turnstile → Continue',
        run: async () => {
          // Continue stays disabled until Turnstile issues a token.
          let btn
          try {
            btn = await waitFor(() => enabledButtonWithText(/^Continue$/), { timeout: 30000 })
          } catch {
            throw new Error('Cloudflare Turnstile did not clear (Continue stayed disabled). If this persists, the QA account may need a Turnstile allowance.')
          }
          btn.click()
          try {
            await waitFor(() => visibleByPlaceholder('Enter your password'), { timeout: 15000 })
          } catch {
            throw new Error('Password step did not appear — the account may not exist or the email probe failed.')
          }
          return 'bot check cleared'
        },
      },
      {
        title: 'Enter password',
        hint: 'type password',
        run: async (cfg) => {
          const pw = requireField(cfg.password, 'password')
          const input = await waitFor(() => visibleByPlaceholder('Enter your password'), { timeout: 10000 })
          await humanType(input, pw)
          return 'password entered'
        },
      },
      {
        title: 'Sign in',
        hint: 'submit → /v4/signin',
        run: async () => {
          const btn = await waitFor(() => enabledButtonWithText(/^(Continue|Signing In)/), { timeout: 10000 })
          btn.click()
          return 'submitted'
        },
      },
      {
        title: 'Verify signed in',
        hint: 'land on /access',
        run: async () => {
          const end = Date.now() + 25000
          while (Date.now() < end) {
            const path = location.pathname
            if (/\/access|\/quick-user-payment/.test(path)) return `signed in → ${path}`
            if (/newMobile-verification|newVerification-code|email-verification/.test(path)) {
              throw new Error(`Hit a verification wall (${path}) — use a QA account without MFA / with a verified email.`)
            }
            if (pageHasText(/Incorrect password|Oops! Something went wrong|couldn't verify your email/i)) {
              throw new Error('Login rejected — check the credentials.')
            }
            if (pageHasText(/Ready to watch new content added by your projectors/i)) return 'signed in (welcome shown)'
            await sleep(500)
          }
          throw new Error(`No success signal within 25s (still on ${location.pathname}).`)
        },
      },
    ],
  },
}

/* ---------- controller messaging ---------- */
export function initQaBridge() {
  let params
  try { params = new URLSearchParams(window.location.search) } catch { return }
  if (params.get('qa_bridge') !== '1') return

  try { window.__pjQaBridge = 'active' } catch { /* noop */ }
  // eslint-disable-next-line no-console
  console.info('[qa-bridge] active on', window.location.pathname)

  // Fresh session every run — before React boots and reads the token.
  clearAuthForFreshSession()

  const controller = window.opener && window.opener !== window ? window.opener : window.parent
  if (!controller || controller === window) return

  const post = (msg) => { try { controller.postMessage({ __pjqa: 1, ...msg }, '*') } catch { /* gone */ } }
  let busy = false

  async function runFlow(id, config) {
    const flow = FLOWS[id]
    if (!flow) { post({ type: 'run-end', ok: false, summary: `Unknown test "${id}"` }); return }
    post({ type: 'run-start', test: id, steps: flow.steps.map((s) => ({ title: s.title, hint: s.hint })) })
    let failedAt = -1
    for (let i = 0; i < flow.steps.length; i++) {
      post({ type: 'step', index: i, status: 'running' })
      const t0 = Date.now()
      try {
        const detail = await flow.steps[i].run(config)
        post({ type: 'step', index: i, status: 'done', detail: typeof detail === 'string' ? detail : undefined, ms: Date.now() - t0 })
      } catch (err) {
        post({ type: 'step', index: i, status: 'failed', error: (err && err.message) || String(err), ms: Date.now() - t0 })
        failedAt = i
        break
      }
    }
    post({
      type: 'run-end',
      ok: failedAt === -1,
      summary: failedAt === -1 ? `All ${flow.steps.length} steps passed` : `Failed at step ${failedAt + 1} — ${flow.steps[failedAt].title}`,
    })
  }

  window.addEventListener('message', (e) => {
    if (!ALLOWED_ORIGINS.includes(e.origin)) return
    const d = e.data
    if (!d || !d.__pjqa) return
    if (d.type === 'run' && !busy) {
      busy = true
      Promise.resolve(runFlow(d.test, d.config || {})).finally(() => { busy = false })
    }
  })

  // Announce readiness (and re-announce shortly after, in case the controller
  // attaches its listener a beat later).
  const flows = Object.entries(FLOWS).map(([id, f]) => ({ id, title: f.title, outline: f.steps.map((s) => ({ title: s.title, hint: s.hint })) }))
  post({ type: 'ready', flows })
  setTimeout(() => post({ type: 'ready', flows }), 800)
}
